import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { canSee, ROLE_LABEL } from "@/lib/permissions";
import {
    LayoutGrid, CalendarDays, CalendarClock, ClipboardList, Users, FolderGit2,
    Receipt, Zap, LineChart, Settings, LogOut, Music,
    UserRound, ChevronDown, Bell, MessageCircle, ExternalLink, Moon, Sun,
    PanelLeftClose, PanelLeftOpen, Building2,
} from "lucide-react";
import { initials } from "@/lib/format";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useTheme } from "next-themes";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api";
import releaseManifest from "@/release-manifest.json";

const NAV_PRIMARY = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutGrid, key: "dashboard" },
    { to: "/calendar", label: "Calendar", icon: CalendarDays, key: "calendar" },
    { to: "/availability", label: "Availability", icon: CalendarClock, key: "availability" },
    { to: "/bookings", label: "Bookings", icon: ClipboardList, key: "bookings" },
    { to: "/crm", label: "Contacts", icon: Users, key: "crm" },
    { to: "/projects", label: "Projects", icon: FolderGit2, key: "projects" },
    { to: "/invoices", label: "Invoices", icon: Receipt, key: "invoices" },
];
const NAV_SECONDARY = [
    { to: "/automations", label: "Automations", icon: Zap, key: "automations" },
    { to: "/analytics", label: "Analytics", icon: LineChart, key: "analytics" },
    { to: "/admin", label: "Studio settings", icon: Settings, key: "admin" },
];

function Brand({ compact }) {
    return (
        <Link to="/dashboard" className={`flex items-center gap-2 ${compact ? "justify-center" : ""}`} data-testid="sidebar-brand" title={compact ? "Dashboard" : undefined}>
            <div className="studio-signal flex h-9 w-9 items-center justify-center rounded-xl bg-foreground text-background">
                <Music className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <div className={`flex flex-col leading-tight ${compact ? "hidden" : ""}`}>
                <span className="font-display text-lg font-semibold tracking-tight">Musook</span>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Studio OS · {releaseManifest.release_identifier}</span>
            </div>
        </Link>
    );
}

function NavItem({ to, label, icon: Icon, compact }) {
    return (
        <NavLink
            to={to}
            data-testid={`sidebar-nav-item-${to.replace("/", "")}`}
            title={compact ? label : undefined}
            className={({ isActive }) =>
                `group flex items-center ${compact ? "justify-center px-2" : "gap-2.5 px-3"} rounded-md py-2 text-sm font-medium transition-colors duration-150 ${
                    isActive
                        ? "bg-primary/10 text-primary border border-primary/15"
                        : "text-muted-foreground hover:bg-card hover:text-foreground"
                }`
            }
        >
            <Icon className="h-5 w-5 shrink-0" strokeWidth={1.75} aria-hidden="true" />
            {!compact && label}
        </NavLink>
    );
}

function SidebarInner({ user, isDark, toggleTheme, compact = false, showToggle = false, toggleSidebar, unreadCount = 0, navigate, handleLogout }) {
    const nav = NAV_PRIMARY.filter((n) => canSee(user, n.key));
    const nav2 = NAV_SECONDARY.filter((n) => canSee(user, n.key));
    const isClient = user?.role === "client";
    const profile = user?.profile || {};
    return (
        <div className={`flex h-full flex-col gap-4 ${compact ? "p-3" : "p-4"}`}>
            <div className={`flex items-center ${compact ? "justify-center" : "justify-between"}`}>
                <Brand compact={compact} />
                {showToggle && !compact && (
                    <button
                        type="button"
                        onClick={toggleSidebar}
                        data-testid="sidebar-collapse-toggle"
                        aria-label="Collapse sidebar"
                        title="Collapse sidebar"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-card hover:text-foreground"
                    >
                        <PanelLeftClose className="h-5 w-5" strokeWidth={1.8} />
                    </button>
                )}
            </div>
            {showToggle && compact && (
                <div className="flex justify-center">
                    <button
                        type="button"
                        onClick={toggleSidebar}
                        data-testid="sidebar-collapse-toggle"
                        aria-label="Expand sidebar"
                        title="Expand sidebar"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-card hover:text-foreground"
                    >
                        <PanelLeftOpen className="h-5 w-5" strokeWidth={1.8} />
                    </button>
                </div>
            )}
            <div className={`mt-2 flex items-center gap-2 rounded-lg border border-border bg-card/60 ${compact ? "justify-center px-2 py-2" : "px-3 py-2"}`}>
                {compact ? <Building2 className="h-4 w-4 text-muted-foreground" strokeWidth={1.8} /> : <div className="h-2 w-2 rounded-full bg-primary" />}
                <div className={`text-xs ${compact ? "hidden" : ""}`}>
                    <div className="font-medium">{user?.organization_name || "Musook workspace"}</div>
                    <div className="text-muted-foreground">{user?.authorization?.location_ids?.length ? `${user.authorization.location_ids.length} authorized studio${user.authorization.location_ids.length === 1 ? "" : "s"}` : "Personal workspace"}</div>
                </div>
            </div>
            {isClient ? (
                <nav className="flex flex-col gap-1">
                    <NavItem to="/portal" label="My studio" icon={UserRound} compact={compact} />
                </nav>
            ) : (
                <>
                    <nav className="flex flex-col gap-1">
                        {nav.map(({ key, ...rest }) => (
                            <NavItem key={rest.to} {...rest} compact={compact} />
                        ))}
                    </nav>
                    <div className="pt-2">
                        {!compact && <div className="px-3 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground">Studio</div>}
                        <nav className="flex flex-col gap-1">
                            {nav2.map(({ key, ...rest }) => (
                                <NavItem key={rest.to} {...rest} compact={compact} />
                            ))}
                        </nav>
                    </div>
                </>
            )}
            <div className="mt-auto" />
            <div className="border-t border-border pt-3 lg:pb-14">
                <button
                    type="button"
                    onClick={toggleTheme}
                    data-testid="sidebar-theme-toggle"
                    aria-pressed={isDark}
                    title={isDark ? "Switch to light mode" : "Switch to dark mode"}
                    className={`flex items-center gap-2 rounded-lg border border-border bg-card/60 px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-card hover:text-foreground ${compact ? "justify-center px-2 lg:fixed lg:bottom-[7rem] lg:left-3 lg:z-40 lg:w-12" : "lg:fixed lg:bottom-[7rem] lg:left-4 lg:z-40 lg:w-[232px]"}`}
                >
                    {isDark ? <Sun className="h-4 w-4 text-accent" strokeWidth={1.8} /> : <Moon className="h-4 w-4 text-primary" strokeWidth={1.8} />}
                    {!compact && <span>{isDark ? "Light mode" : "Dark mode"}</span>}
                </button>
                <Link
                    to="/inbox"
                    data-testid="sidebar-notifications"
                    title={compact ? `Notifications${unreadCount > 0 ? ` (${unreadCount})` : ""}` : undefined}
                    className={`relative mt-2 flex items-center gap-2 rounded-lg border border-border bg-card/60 px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-card hover:text-foreground ${compact ? "justify-center px-2 lg:fixed lg:bottom-16 lg:left-3 lg:z-40 lg:w-12" : "lg:fixed lg:bottom-16 lg:left-4 lg:z-40 lg:w-[232px]"}`}
                >
                    <Bell className="h-4 w-4 shrink-0" strokeWidth={1.8} />
                    {!compact && <span className="flex-1">Notifications</span>}
                    {unreadCount > 0 && <span className={`${compact ? "absolute right-1 top-0" : "ml-auto"} min-w-4 rounded-full bg-primary px-1 text-center text-[9px] font-semibold leading-4 text-primary-foreground`}>{unreadCount > 9 ? "9+" : unreadCount}</span>}
                </Link>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="ghost"
                            className={`mt-1 h-11 w-full rounded-lg border border-transparent px-2 hover:border-border hover:bg-card ${compact ? "justify-center lg:fixed lg:bottom-3 lg:left-3 lg:z-40 lg:w-12" : "justify-start gap-2 lg:fixed lg:bottom-3 lg:left-4 lg:z-40 lg:w-[232px]"}`}
                            data-testid="sidebar-profile-menu"
                            aria-label="Open profile menu"
                            title={compact ? (profile.display_name || user?.name || "Profile") : undefined}
                        >
                            <Avatar className="h-8 w-8 shrink-0">
                                <AvatarImage src={profile.avatar_url || user?.picture || ""} />
                                <AvatarFallback className="bg-primary/10 text-[10px] text-primary">{initials(profile.display_name || user?.name)}</AvatarFallback>
                            </Avatar>
                            {!compact && <><span className="min-w-0 flex-1 truncate text-left text-xs font-medium">{profile.display_name || user?.name}</span><ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /></>}
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent side="top" align={compact ? "end" : "start"} className="w-64">
                        <DropdownMenuLabel>
                            <div className="flex items-center gap-2">
                                <Avatar className="h-9 w-9"><AvatarImage src={profile.avatar_url || user?.picture || ""} /><AvatarFallback className="bg-primary/10 text-xs text-primary">{initials(profile.display_name || user?.name)}</AvatarFallback></Avatar>
                                <div className="min-w-0"><div className="truncate font-medium">{profile.display_name || user?.name}</div><div className="truncate text-xs font-normal text-muted-foreground">{ROLE_LABEL[user?.role] || user?.role}</div></div>
                            </div>
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => navigate("/profile/edit")}><UserRound className="mr-2 h-4 w-4" /> Edit profile</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(profile.slug ? `/profile/${profile.slug}` : "/profile/edit")}><ExternalLink className="mr-2 h-4 w-4" /> View public profile</DropdownMenuItem>
                        {canSee(user, "admin") && <DropdownMenuItem onClick={() => navigate("/settings")}><Settings className="mr-2 h-4 w-4" /> Workspace settings</DropdownMenuItem>}
                        {canSee(user, "team") && <DropdownMenuItem onClick={() => navigate("/team")}><Users className="mr-2 h-4 w-4" /> Manage team</DropdownMenuItem>}
                        <DropdownMenuItem onClick={() => navigate("/inbox")}><Bell className="mr-2 h-4 w-4" /> Inbox{unreadCount > 0 ? ` (${unreadCount})` : ""}</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={handleLogout} data-testid="sidebar-logout"><LogOut className="mr-2 h-4 w-4" /> Sign out</DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
    );
}

export default function AppShell() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { resolvedTheme, setTheme } = useTheme();
    const [open, setOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
        try {
            return window.localStorage.getItem("musook-sidebar") === "collapsed";
        } catch {
            return false;
        }
    });
    const isDark = resolvedTheme === "dark";
    const toggleTheme = () => setTheme(isDark ? "light" : "dark");
    const toggleSidebar = () => {
        setSidebarCollapsed((collapsed) => {
            const next = !collapsed;
            try {
                window.localStorage.setItem("musook-sidebar", next ? "collapsed" : "expanded");
            } catch {
                // Keep the toggle usable if browser storage is unavailable.
            }
            return next;
        });
    };
    const { data: notifications = [] } = useQuery({
        queryKey: ["notifications"],
        queryFn: () => apiGet("/notifications"),
        enabled: !!user,
    });
    const unreadCount = notifications.filter((item) => !item.read).length;
    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };
    return (
        <div className="flex h-screen overflow-hidden bg-[hsl(var(--paper))] musook-page">
            {/* Sidebar */}
            <aside className={`hidden h-screen shrink-0 overflow-y-auto border-r border-border bg-[hsl(var(--sidebar-bg))] transition-[width] duration-200 lg:flex ${sidebarCollapsed ? "w-[72px]" : "w-[264px]"}`}>
                <div className="min-h-full w-full">
                    <SidebarInner user={user} isDark={isDark} toggleTheme={toggleTheme} compact={sidebarCollapsed} showToggle toggleSidebar={toggleSidebar} unreadCount={unreadCount} navigate={navigate} handleLogout={handleLogout} />
                </div>
            </aside>
            <div className="flex h-screen min-w-0 flex-1 flex-col">
                {/* Utility bar: navigation stays in the rail; mobile keeps only the menu trigger here. */}
                <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-[hsl(var(--paper)/.88)] px-4 backdrop-blur lg:hidden" data-testid="utility-bar">
                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <Button variant="ghost" size="icon" className="lg:hidden" data-testid="topbar-mobile-menu" aria-label="Open navigation">
                                <LayoutGrid className="h-5 w-5" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="left" className="w-[280px] overflow-y-auto p-0" data-testid="mobile-sidebar">
                            <SidebarInner user={user} isDark={isDark} toggleTheme={toggleTheme} unreadCount={unreadCount} navigate={navigate} handleLogout={handleLogout} />
                        </SheetContent>
                    </Sheet>
                    <div className="font-display text-lg font-semibold tracking-tight lg:hidden">Musook</div>
                </header>
                <main className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto pretty-scroll">
                    <Outlet />
                </main>
            </div>
            {location.pathname !== "/chat" && (
                <Link
                    to="/chat"
                    data-testid="floating-assistant-button"
                    aria-label="Open booking assistant"
                    title="Open booking assistant"
                    className="fixed bottom-5 right-5 z-50 inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-xl shadow-primary/25 ring-4 ring-background/80 transition-transform duration-200 hover:scale-105 hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                    <MessageCircle className="h-6 w-6" strokeWidth={1.9} aria-hidden="true" />
                </Link>
            )}
        </div>
    );
}
